import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import branding from '../branding.json';
import homeData from '../homedata.json';
import { useInView } from 'react-intersection-observer';
import { FiUsers, FiCode, FiLayers, FiCpu, FiTool, FiBox, FiFileText, FiTrendingUp } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

// Import our components
import { HeroSection } from '../components/home/HeroSection';
import { StatsSection } from '../components/home/StatsSection';
import { FeaturesSection } from '../components/home/FeaturesSection';
import { PersonaCarousel } from '../components/personas/PersonaCarousel';
import { ActionCards } from '../components/home/ActionCards';
import { HowItWorksSection } from '../components/home/HowItWorksSection';
import { CtaSection } from '../components/home/CtaSection';

export const Landing: React.FC = () => {
  const { darkMode } = useTheme();
  const { openLoginModal } = useAuth();
  
  // Refs for each section
  const heroRef = useRef<HTMLElement>(null);
  const actionCardsRef = useRef<HTMLElement>(null);
  const statsRef = useRef<HTMLElement>(null);
  const personasRef = useRef<HTMLElement>(null);
  const howItWorksRef = useRef<HTMLElement>(null);
  const useCasesRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);
  
  // Set document title based on branding
  useEffect(() => {
    document.title = branding.appName;
  }, []);
  
  // Intersection observer hooks for scroll animations
  const [featuredInViewRef, featuredInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [howItWorksInViewRef, howItWorksInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [useCasesInViewRef, useCasesInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [ctaInViewRef, ctaInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  // Map icon strings to actual components
  const getIconComponent = (iconName: string) => {
    const iconMap = {
      'FiUsers': FiUsers,
      'FiCode': FiCode,
      'FiLayers': FiLayers,
      'FiCpu': FiCpu,
      'FiTool': FiTool,
      'FiBox': FiBox,
      'FiFileText': FiFileText,
      'FiTrendingUp': FiTrendingUp
    };
    return iconMap[iconName] || FiUsers;
  };
  
  // Process action cards data
  const actionCards = homeData.actionCards.cards.map(card => ({
    ...card,
    icon: getIconComponent(card.icon)
  }));
  
  // Process use cases data
  const useCases = homeData.useCases.cases.map(useCase => ({
    ...useCase,
    icon: getIconComponent(useCase.icon)
  }));
  
  // Scroll to section handler with offset for fixed header
  const scrollToSection = (ref: React.RefObject<HTMLElement>) => {
    if (ref.current) {
      const headerHeight = 80; // Approximate header height
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  
  // Click handlers
  const handleHeroCtaClick = () => {
    openLoginModal();
  };
  
  const handleHeroSecondaryClick = () => {
    scrollToSection(howItWorksRef);
  };
  
  // Add a document-level click handler to capture clicks on static elements
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      // Find the closest anchor tag
      const anchor = (e.target as HTMLElement).closest('a');
      
      // If it's a static link (from the landing page)
      if (anchor && anchor.getAttribute('href') === '#' && !anchor.onclick) {
        e.preventDefault();
        openLoginModal();
      }
    };
    
    document.addEventListener('click', handleDocumentClick);
    
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [openLoginModal]);
  
  // Update header links to point to the correct sections
  useEffect(() => {
    // Find all header links
    const headerLinks = document.querySelectorAll('a[href^="#"]');
    
    // Add click handlers to scroll to the correct section
    headerLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href')?.substring(1);
        
        if (targetId) {
          const targetRef = {
            'hero': heroRef,
            'action-cards': actionCardsRef,
            'stats': statsRef,
            'personas': personasRef,
            'how-it-works': howItWorksRef,
            'use-cases': useCasesRef,
            'cta': ctaRef,
            'features': useCasesRef // Map "features" to "use-cases" for header link
          }[targetId];
          
          if (targetRef) {
            scrollToSection(targetRef);
          }
        }
      });
    });
  }, []);
  
  return (
    <div className={`${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
      {/* Hero Section */}
      <section id="hero" ref={heroRef}>
        <HeroSection 
        taglines={branding.theme.animation?.taglines || []}
        onCtaClick={handleHeroCtaClick}
        darkMode={darkMode}
        primaryCtaText={homeData.hero.primaryCtaText}
        secondaryCtaText={homeData.hero.secondaryCtaText}
        onSecondaryCtaClick={handleHeroSecondaryClick}
        title={homeData.hero.title}
        subtitle={homeData.hero.subtitle}
      />
      </section>
      
      {/* Action Cards */}
      <section id="action-cards" ref={actionCardsRef}>
        <ActionCards 
        darkMode={darkMode} 
        title={homeData.actionCards.title}
        cards={actionCards}
        staticMode={true}
      />
      </section>
      
      {/* Stats Section */}
      <section id="stats" ref={statsRef}>
        <StatsSection 
        darkMode={darkMode} 
        stats={homeData.stats}
      />
      </section>
      
      {/* Featured Personas Section */}
      <section id="personas" ref={personasRef}>
        <div ref={featuredInViewRef}>
          <PersonaCarousel 
          personas={homeData.featuredPersonas.personas}
          darkMode={darkMode}
          inView={featuredInView}
          title={homeData.featuredPersonas.title}
          subtitle={homeData.featuredPersonas.subtitle}
          staticData={true}
          />
        </div>
      </section>
      
      {/* How It Works Section */}
      <section id="how-it-works" ref={howItWorksRef}>
        <div ref={howItWorksInViewRef}>
          <HowItWorksSection 
          darkMode={darkMode} 
          inView={howItWorksInView}
          title={homeData.howItWorks.title}
          steps={homeData.howItWorks.steps}
          appName={branding.appName}
          />
        </div>
      </section>
      
      {/* Use Cases Section */}
      <section id="use-cases" ref={useCasesRef}>
        <div ref={useCasesInViewRef}>
          <FeaturesSection 
          darkMode={darkMode} 
          inView={useCasesInView}
          title={homeData.useCases.title}
          useCases={useCases}
          />
        </div>
      </section>
      
      {/* CTA Section */}
      <section id="cta" ref={ctaRef}>
        <div ref={ctaInViewRef}>
          <CtaSection 
          darkMode={darkMode}
          inView={ctaInView}
          title={homeData.cta.title}
          description={homeData.cta.description}
          primaryButtonText={homeData.cta.primaryButtonText}
          secondaryButtonText={homeData.cta.secondaryButtonText}
          staticMode={true}
          />
        </div>
      </section>
      
      {/* Version info from branding */}
      <div className={`text-xs py-6 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        {branding.meta.engine} v{branding.meta.version}
      </div>
    </div>
  );
};
